import {
  PhoneNumberUtil,
  PhoneNumberFormat,
} from "google-libphonenumber";
import { Contact } from "../../../common/ContactForm";
import { Country, State } from "../../../../services/locationService";
import { AddressFormData } from "./types";

// Phone validation utility
const phoneUtil = PhoneNumberUtil.getInstance();

// Get location string from various formats
export const getLocationString = (location: any): string => {
  if (!location) return "";
  if (typeof location === "string") return location;
  if (typeof location === "object" && location.name) return location.name;
  return "";
};

// Create empty form
export const createEmptyForm = (): AddressFormData => ({
  nickname: "",
  contactName: "",
  companyName: "",
  email: "",
  phone: "",
  nationalId: "",
  street: "",
  houseNo: "",
  zipCode: "",
  deliveryDetails: "",
  country_id: null,
  state_id: null,
  state_other: "",
  city_id: null,
  city_other: "",
});

// Validate phone number
export const isValidPhoneNumber = (
  phoneNumber: string,
  countryCode?: string
): boolean => {
  if (!phoneNumber || phoneNumber.trim() === "") return true; // Allow empty phone numbers

  // Clean the phone number - remove any non-digit characters except + at the beginning
  const cleanedNumber = phoneNumber.replace(/[^\d+]/g, '');
  if (!cleanedNumber || cleanedNumber.length < 3) return false;

  try {
    // Try parsing with the provided country code first
    if (countryCode) {
      const parsedNumber = phoneUtil.parse(cleanedNumber, countryCode.toUpperCase());
      if (phoneUtil.isValidNumber(parsedNumber)) {
        return true;
      }
    }

    // If that fails, try parsing as an international number (with +)
    if (cleanedNumber.startsWith('+')) {
      const parsedNumber = phoneUtil.parse(cleanedNumber, "ZZ");
      return phoneUtil.isValidNumber(parsedNumber);
    }

    // Fall back to US format
    const parsedNumber = phoneUtil.parse(cleanedNumber, "US");
    return phoneUtil.isValidNumber(parsedNumber);
  } catch (error: any) {
    console.warn("Phone validation error:", error.message, "for number:", phoneNumber);
    // If we can't parse it with google-libphonenumber, it's likely invalid
    // Only allow very basic patterns as a last resort
    const basicPattern = /^\+?[\d\s\-()]{10,15}$/;
    const hasValidLength = phoneNumber.trim().length >= 10 && phoneNumber.trim().length <= 15;
    return basicPattern.test(phoneNumber.trim()) && hasValidLength;
  }
};

// Format phone number
export const formatPhoneNumber = (
  phoneNumber: string,
  countryCode?: string
): string => {
  if (!phoneNumber || phoneNumber.trim() === "") return "";

  // Clean the phone number - remove any non-digit characters except + at the beginning
  const cleanedNumber = phoneNumber.replace(/[^\d+]/g, '');
  if (!cleanedNumber || cleanedNumber.length < 3) return phoneNumber;

  try {
    let parsedNumber = null;

    // Try parsing with the provided country code first
    if (countryCode) {
      try {
        parsedNumber = phoneUtil.parse(cleanedNumber, countryCode.toUpperCase());
        if (phoneUtil.isValidNumber(parsedNumber)) {
          return phoneUtil.format(parsedNumber, PhoneNumberFormat.INTERNATIONAL);
        }
      } catch {
        // Continue to other parsing attempts
      }
    }

    // If that fails, try parsing as an international number (with +)
    if (cleanedNumber.startsWith('+')) {
      try {
        parsedNumber = phoneUtil.parse(cleanedNumber, "ZZ");
        if (phoneUtil.isValidNumber(parsedNumber)) {
          return phoneUtil.format(parsedNumber, PhoneNumberFormat.INTERNATIONAL);
        }
      } catch {
        // Continue to fallback
      }
    }

    // Fall back to US format
    parsedNumber = phoneUtil.parse(cleanedNumber, "US");
    if (phoneUtil.isValidNumber(parsedNumber)) {
      return phoneUtil.format(parsedNumber, PhoneNumberFormat.INTERNATIONAL);
    }

    return phoneNumber; // Return original if invalid
  } catch (error: any) {
    console.warn("Phone formatting error:", error.message, "for number:", phoneNumber);
    return phoneNumber; // Return original if parsing fails
  }
};

// Helper function to convert AddressFormData to Contact object
export const createContactFromForm = (
  formData: AddressFormData,
  countries: Country[],
  states: State[] = [],
  existingContact?: Contact | null
): Contact => {
  const country = countries.find(c => c.id === formData.country_id);
  const state = states.find(s => s.id === formData.state_id);

  const baseContact: Partial<Contact> = existingContact
    ? { ...existingContact }
    : {};
  const resolvedId =
    baseContact.id || existingContact?.id || `temp_${Date.now()}_${Math.random()}`;

  return {
    ...baseContact,
    id: resolvedId,
    nickname: formData.nickname || baseContact.nickname,
    contactName: formData.contactName,
    companyName: formData.companyName,
    email: formData.email,
    phone: formData.phone,
    nationalId: formData.nationalId || baseContact.nationalId || '',
    street: formData.street,
    houseNo: formData.houseNo,
    zipCode: formData.zipCode,
    deliveryDetails: formData.deliveryDetails,
    country_id: formData.country_id,
    state_id: formData.state_id,
    city_id: formData.city_id,
    city_other: formData.city_other,
    city: formData.city_other || baseContact.city || '',
    country: country?.name || baseContact.country || '',
    countryCode: country?.iso2 || baseContact.countryCode || '',
    // Add state code and state name for FedEx API
    state: state?.name || formData.state_other || baseContact.state || '',
    stateCode: state?.state_code || baseContact.stateCode || '',
    isTemporary: existingContact ? existingContact.isTemporary ?? false : true,
  };
};
